import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DeliveryNoteForm from '../../components/Form/DeliveryNoteForm';
import DeliveryNoteLetterGenerator from '../../components/HR/DeliveryNoteLetterGenerator';
import Button from '../../components/Form/Button';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { DeliveryNote, DeliveryNoteItem } from '../../types';
import { useDeliveryNotes, useCreateDeliveryNote, useUpdateDeliveryNote, useDeleteDeliveryNote } from '../../hooks/useDeliveryNotes';
import { useRequisitions, useUpdateRequisition } from '../../hooks/useRequisitions';
import { useToast } from '../../contexts/ToastContext';
import { linksFromDeliveryNote, syncRequisitionItemStatus } from '../../utils/requisitionSync';
import { useDeliveryNoteFilters } from './useDeliveryNoteFilters';
import DeliveryNoteItemsRow from './DeliveryNoteItemsRow';

const DeliveryNotes: React.FC = () => {
  const { showToast } = useToast();
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 },
    populate: '*'
  }), []);

  const { data: deliveryNotes = [], isLoading, error, refetch } = useDeliveryNotes(queryOptions);
  const { data: requisitions = [], refetch: refetchReqs } = useRequisitions(queryOptions);
  const updateReqMutation = useUpdateRequisition();

  const createMutation = useCreateDeliveryNote(() => {
    showToast('Delivery note created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDeliveryNote(undefined);
  });
  const updateMutation = useUpdateDeliveryNote(() => {
    showToast('Delivery note updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDeliveryNote(undefined);
  });
  const deleteMutation = useDeleteDeliveryNote(() => {
    showToast('Delivery note deleted successfully', 'success');
    refetch();
  });

  const [showForm, setShowForm] = useState(false);
  const [editingDeliveryNote, setEditingDeliveryNote] = useState<DeliveryNote | undefined>();
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);
  const [selectedDeliveryNoteForLetter, setSelectedDeliveryNoteForLetter] = useState<DeliveryNote | undefined>();
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requisitionId = searchParams.get('requisitionId');

  React.useEffect(() => {
    if (requisitionId && !showForm && !editingDeliveryNote) setShowForm(true);
  }, [requisitionId, showForm, editingDeliveryNote]);

  // requisitionId may hold a documentId or a numeric id depending on how the
  // note was created, so match against both
  const getRequisitionInfo = useCallback((reqId?: string) => {
    if (!reqId) return 'Unknown Requisition';
    const idStr = String(reqId);
    const req = requisitions.find(
      (r: any) => String(r.documentId || '') === idStr || String(r.id) === idStr
    );
    return req ? `${req.formNumber} - ${req.vessel}` : 'Unknown Requisition';
  }, [requisitions]);

  const { searchTerm, setSearchTerm, filteredDeliveryNotes, clearFilters, filterConfigs } =
    useDeliveryNoteFilters(deliveryNotes, requisitions, getRequisitionInfo);

  const exportData = useMemo(() => filteredDeliveryNotes.map(n => ({
    'Delivery Note #': n.deliveryNoteNumber, 'Requisition': getRequisitionInfo(n.requisitionId),
    'Vessel/Headquarters': n.vessel, 'Work Site': n.workSite, 'Project Location': n.projectLocation,
    'Delivery Date': new Date(n.date || n.createdAt).toLocaleDateString(), 'Status': n.status
  })), [filteredDeliveryNotes, getRequisitionInfo]);
  const handleExport = () => exportToExcel(exportData, `delivery_notes_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(exportData, 'Delivery Notes');

  const handleEdit = (dn: DeliveryNote) => { setEditingDeliveryNote(dn); setShowForm(true); };
  const handleDelete = async (dn: DeliveryNote) => { if (window.confirm(`Delete delivery note ${dn.deliveryNoteNumber}?`)) { try { await deleteMutation.execute(dn); } catch (e) { console.error(e); } } };
  const handleAdd = () => { setEditingDeliveryNote(undefined); setShowForm(true); };
  const handleFormSubmit = async (data: Partial<DeliveryNote>) => {
    try {
      if (editingDeliveryNote) await updateMutation.execute({ id: parseInt(editingDeliveryNote.id), data: { ...editingDeliveryNote, ...data } });
      else { await createMutation.execute(data); await bumpCodePrefixSequence(data.deliveryNoteNumber); }
      if (requisitionId) navigate('/delivery-notes');
    } catch (e) { console.error(e); }
  };
  const handleFormCancel = () => { setShowForm(false); setEditingDeliveryNote(undefined); if (requisitionId) navigate('/requisitions'); };
  const handleGenerateLetter = (dn: DeliveryNote) => { setSelectedDeliveryNoteForLetter(dn); setShowLetterGenerator(true); };
  const handleLetterCancel = () => { setShowLetterGenerator(false); setSelectedDeliveryNoteForLetter(undefined); };
  const handleStatusUpdate = async (dn: DeliveryNote, newStatus: DeliveryNote['status']) => {
    try {
      // Delivering the whole note marks every item and requisition item as delivered
      const technicalPart = newStatus === 'delivered'
        ? (dn.technicalPart || []).map((i: any) => ({ ...i, status: 'delivered' })) : dn.technicalPart;
      await updateMutation.execute({ id: parseInt(dn.id), data: { ...dn, technicalPart, status: newStatus, ...(newStatus === 'received' && { receivedDate: new Date().toISOString().split('T')[0] }) } });
      if (newStatus === 'delivered') {
        const count = await syncRequisitionItemStatus(
          requisitions, updateReqMutation.execute, linksFromDeliveryNote(dn), 'delivered',
          { deliveryDate: new Date().toISOString().split('T')[0] }
        );
        if (count > 0) showToast(`${count} requisition item(s) marked as delivered`, 'success');
        refetchReqs();
      }
    } catch (e) { console.error(e); }
  };

  /** Mark one item of a note delivered; the linked requisition item follows,
   *  and the note flips to delivered once every item is */
  const handleItemDelivered = async (dn: DeliveryNote, item: DeliveryNoteItem) => {
    try {
      const technicalPart = (dn.technicalPart || []).map((i: any) =>
        String(i.requisitionItemId || i.id) === String(item.requisitionItemId || item.id)
          ? { ...i, status: 'delivered' } : i);
      const allDelivered = technicalPart.every((i: any) => i.status === 'delivered');
      const status = allDelivered && dn.status === 'pending' ? 'delivered' : dn.status;
      await updateMutation.execute({ id: parseInt(dn.id), data: { ...dn, documentId: dn.documentId, technicalPart, status } });
      if (dn.requisitionId) {
        await syncRequisitionItemStatus(
          requisitions, updateReqMutation.execute,
          [{ requisitionId: String(dn.requisitionId), itemId: String(item.requisitionItemId || item.id) }],
          'delivered', { deliveryDate: new Date().toISOString().split('T')[0] }
        );
        refetchReqs();
      }
      showToast(`Item "${item.type}" marked as delivered`, 'success');
    } catch (e: any) { showToast(e?.message || 'Failed to update item', 'error'); }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div><p className="text-gray-600">Loading delivery notes...</p></div></div>
  );
  if (error) return <ErrorState message="Error loading delivery notes" onRetry={refetch} entityName="delivery notes" />;
  if (showForm) return <div className="space-y-6"><DeliveryNoteForm deliveryNote={editingDeliveryNote} requisitionId={requisitionId || undefined} onSubmit={handleFormSubmit} onCancel={handleFormCancel} /></div>;
  if (showLetterGenerator && selectedDeliveryNoteForLetter) return <DeliveryNoteLetterGenerator deliveryNote={selectedDeliveryNoteForLetter} requisitionInfo={getRequisitionInfo(selectedDeliveryNoteForLetter.requisitionId)} onClose={handleLetterCancel} onGenerate={handleLetterCancel} />;

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Delivery Notes" description="Manage delivery notes for purchased requisitions" />
        {requisitionId && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Creating delivery note for requisition: <strong>{getRequisitionInfo(requisitionId)}</strong>
            </p>
          </div>
        )}
      </div>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search delivery notes..."
        filters={filterConfigs}
        onClearFilters={clearFilters}
        totalCount={deliveryNotes.length}
        filteredCount={filteredDeliveryNotes.length}
        entityName="Delivery Notes"
        onAdd={handleAdd}
        addLabel="Add New Delivery Note"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(['pending', 'delivered', 'received', 'completed'] as const).map(status => {
          const colors: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-800', delivered: 'bg-blue-100 text-blue-800', received: 'bg-green-100 text-green-800', completed: 'bg-purple-100 text-purple-800' };
          return (
            <div key={status} className="card p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-gray-600 capitalize">{status}</p><p className="text-2xl font-semibold text-gray-900">{filteredDeliveryNotes.filter(n => n.status === status).length}</p></div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Delivery Note #', 'Requisition', 'Vessel', 'Work Site', 'Items', 'Delivery Date', 'Status', 'Delivered By', 'Actions'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDeliveryNotes.map((deliveryNote) => (
                <React.Fragment key={deliveryNote.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{deliveryNote.deliveryNoteNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getRequisitionInfo(deliveryNote.requisitionId)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{deliveryNote.vessel}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{deliveryNote.workSite}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button type="button" onClick={() => setExpandedNoteId(expandedNoteId === deliveryNote.id ? null : deliveryNote.id)}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200">
                      {deliveryNote.technicalPart?.length || 0} items {expandedNoteId === deliveryNote.id ? '▲' : '▼'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(deliveryNote.deliveryDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      deliveryNote.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                      deliveryNote.status === 'received' ? 'bg-green-100 text-green-800' :
                      deliveryNote.status === 'delivered' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(deliveryNote.status || 'pending').charAt(0).toUpperCase() + (deliveryNote.status || 'pending').slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{deliveryNote.deliveredBy?.name || (deliveryNote as any).deliveredByName || 'Not assigned'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                    <div className="flex space-x-2">
                      <Button onClick={() => handleEdit(deliveryNote)} variant="secondary" size="sm">Edit</Button>
                      <Button onClick={() => handleDelete(deliveryNote)} variant="danger" size="sm">Delete</Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={() => handleGenerateLetter(deliveryNote)} variant="primary" size="sm" className="w-full">Generate Form</Button>
                    </div>
                    {deliveryNote.status === 'pending' && (
                      <Button onClick={() => handleStatusUpdate(deliveryNote, 'delivered')} variant="primary" size="sm" className="w-full text-xs">Mark All Delivered</Button>
                    )}
                    {deliveryNote.status === 'delivered' && (
                      <Button onClick={() => handleStatusUpdate(deliveryNote, 'received')} variant="primary" size="sm" className="w-full text-xs">Mark Received</Button>
                    )}
                    {deliveryNote.status === 'received' && (
                      <Button onClick={() => handleStatusUpdate(deliveryNote, 'completed')} variant="primary" size="sm" className="w-full text-xs">Mark Completed</Button>
                    )}
                  </td>
                </tr>
                {expandedNoteId === deliveryNote.id && (
                  <DeliveryNoteItemsRow note={deliveryNote} colSpan={9}
                    requisitionInfo={getRequisitionInfo(deliveryNote.requisitionId)} onItemDelivered={handleItemDelivered} />
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {filteredDeliveryNotes.length === 0 && (
            <div className="text-center py-12"><p className="text-gray-500">No delivery notes found</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryNotes;
